'use client';

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import { CHAT_FILESYSTEM_QUERY_KEY } from './api/useChatFilesystem';

const PLAN_FILE_DATA_TYPES = new Set<string>([
  DataMessageTypeEnum.CopilotPlan,
  DataMessageTypeEnum.Todos,
]);

function computePlanFileSignature(messages: TerraUIMessage[]): string | null {
  let count = 0;
  let lastPartId = '';

  for (const message of messages) {
    if (message.role !== 'assistant' || !message.parts) {
      continue;
    }

    for (const part of message.parts) {
      if (part.type !== TerraUIMessageDataPartName) {
        continue;
      }

      const dataType = (part.data as { type?: string } | undefined)?.type;

      if (!dataType || !PLAN_FILE_DATA_TYPES.has(dataType)) {
        continue;
      }

      count += 1;
      lastPartId = `${message.id}:${(part.data as { id?: string }).id ?? count}`;
    }
  }

  return count === 0 ? null : `${count}:${lastPartId}`;
}

export function useCopilotPlanFileSync({
  chatId,
  messages,
}: {
  chatId: string;
  messages: TerraUIMessage[];
}): void {
  const queryClient = useQueryClient();
  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    const signature = computePlanFileSignature(messages);

    if (signature === null || signature === lastSignatureRef.current) {
      return;
    }

    lastSignatureRef.current = signature;

    void queryClient.invalidateQueries({
      queryKey: [CHAT_FILESYSTEM_QUERY_KEY, chatId],
    });
  }, [chatId, messages, queryClient]);
}
